'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { AuthenticationManager } from '@benzinga/session';
import { Watchlist, WatchlistManager, WatchlistManagerEvent, WatchlistSymbol } from '@benzinga/watchlist-manager';
import Hooks from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { WatchlistsDropdown } from './WatchlistsDropdown';
import { TrackingManager } from '@benzinga/tracking-manager';
import { ButtonVariant } from '@benzinga/core-ui';

export interface AddToWatchlistProps {
  symbol: string;
  variant?: 'default' | 'compact';
  buttonText?: string;
  buttonVariant?: ButtonVariant;
  showIcon?: boolean;
  onSuccess?: (action: 'Added' | 'Removed', symbol: string) => void;
}

export const AddToWatchlist: React.FC<AddToWatchlistProps> = ({
  buttonText,
  buttonVariant,
  onSuccess,
  showIcon,
  symbol,
  variant = 'default',
}) => {
  const session = React.useContext(SessionContext);
  const [watchlistManager] = useState(session.getManager(WatchlistManager));
  const [authManager] = useState(session.getManager(AuthenticationManager));
  const [watchlists, setWatchlists] = useState(watchlistManager.getStoredWatchlists());
  const [, setWatchlistInProcess] = useState(false);
  const [watchlistDebounce, setWatchlistDebounce] = useState<number | null>(null);
  const isLoggedIn = useIsUserLoggedIn();
  const update = Hooks.useForceUpdate();

  const actionWithDebounce = useCallback(
    (callback: () => void, ms = 500) => {
      if (watchlistDebounce) {
        clearTimeout(watchlistDebounce);
      }

      const watchlistActionDebounce = window.setTimeout(() => {
        callback();
      }, ms);

      setWatchlistDebounce(watchlistActionDebounce);
    },
    [watchlistDebounce],
  );

  const addFakeSymbol = useCallback(
    (watchlist: Watchlist | undefined) => {
      if (watchlist === undefined) return;

      (watchlist.symbols as Partial<WatchlistSymbol & { fake: boolean }>[]).push({
        fake: true,
        symbol,
      });
      update();
    },
    [symbol, update],
  );

  const removeSymbolVisually = useCallback(
    (watchlist: Watchlist | undefined, removeFake = false) => {
      if (watchlist === undefined) return;
      let watchlistSymbolIndex: number | null = null;

      const watchlistSymbol = (watchlist.symbols as (WatchlistSymbol & { fake: boolean })[]).find((item, index) => {
        let isSymbolFound: boolean;

        if (removeFake) {
          isSymbolFound = item.symbol === symbol && item.fake;
        } else {
          isSymbolFound = item.symbol === symbol;
        }

        if (isSymbolFound) {
          watchlistSymbolIndex = index;
        }

        return isSymbolFound;
      });

      if (watchlistSymbol && watchlistSymbolIndex !== null) {
        watchlist.symbols.splice(watchlistSymbolIndex, 1);
        update();
      }

      return watchlistSymbol as WatchlistSymbol;
    },
    [symbol, update],
  );

  const onSymbolAdded = useCallback(
    (watchlist: Watchlist | undefined) => {
      if (watchlist === undefined) return;
      setWatchlistInProcess(true);
      const watchlistOrigin = { ...watchlist, symbols: [...watchlist.symbols] };

      addFakeSymbol(watchlist);
      session.getManager(TrackingManager).trackWatchlistEvent('add_symbol', {
        symbol,
        watchlist_id: watchlist.watchlistId,
      });

      actionWithDebounce(() => {
        watchlistManager.addTickersToWatchlist(watchlistOrigin, [symbol]).then(() => {
          removeSymbolVisually(watchlist, true);
          setWatchlistInProcess(false);
          onSuccess && onSuccess('Added', symbol);
        });
      });
    },
    [addFakeSymbol, actionWithDebounce, watchlistManager, symbol, removeSymbolVisually, onSuccess, session],
  );

  const onSymbolRemove = useCallback(
    async (watchlist: Watchlist | undefined) => {
      if (watchlist === undefined) return;
      setWatchlistInProcess(true);
      const watchlistOrigin = { ...watchlist, symbols: [...watchlist.symbols] };
      const watchlistSymbol = removeSymbolVisually(watchlist);

      if (watchlistSymbol === undefined) return;

      session.getManager(TrackingManager).trackWatchlistEvent('delete_symbol', {
        symbol,
        watchlist_id: watchlist.watchlistId,
      });

      actionWithDebounce(() => {
        const symbolId = watchlist.symbols.find(item => item.symbol === symbol)?.symbolId;
        if (symbolId) {
          watchlistSymbol.symbolId = symbolId;
        }
        watchlistManager.removeTickersFromWatchlist(watchlistOrigin, [watchlistSymbol]).then(() => {
          setWatchlistInProcess(false);
          onSuccess && onSuccess('Removed', symbol);
        });
      });
    },
    [removeSymbolVisually, actionWithDebounce, watchlistManager, onSuccess, symbol, session],
  );

  Hooks.useSubscriber(watchlistManager, (event: WatchlistManagerEvent) => {
    switch (event.type) {
      case 'watchlist:updated_watchlists':
        setWatchlists(event.watchlists);
        break;
      default:
        break;
    }
  });

  const onButtonClick = useCallback(() => {
    if (!isLoggedIn) {
      authManager.requestAuthorization();
    }
  }, [authManager, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      (async () => {
        const response = await watchlistManager.getWatchlists();

        if (response.err) {
          setWatchlists([]);
        } else {
          setWatchlists(response.ok ?? []);
        }
      })();
    } else {
      setWatchlists([]);
    }
  }, [isLoggedIn, watchlistManager]);

  return (
    <WatchlistsDropdown
      buttonText={buttonText || 'Add To Watchlist +'}
      buttonVariant={buttonVariant}
      onAddedSymbolToWatchlist={onSymbolAdded}
      onClick={onButtonClick}
      onRemovedSymbolFromWatchlist={onSymbolRemove}
      showEmptyList={!!isLoggedIn}
      showIcon={showIcon}
      symbol={symbol}
      variant={variant}
      watchlists={watchlists}
    />
  );
};
